import { z } from "zod";
import { IdSchema } from "./ids.js";
export const TimestampSchema = z.iso.datetime({ offset: true });
/** Supported UI and assistant languages. */
export const LOCALES = ["tr", "en"];
export const LocaleSchema = z.enum(LOCALES);
/** Text that a manifest supplies in every supported locale. */
export const LocalizedTextSchema = z.object({ tr: z.string().min(1), en: z.string().min(1) });
/** Who performed an action. `system` is the kernel itself (migrations, schedulers). */
export const ActorRefSchema = z.object({
    kind: z.enum(["user", "agent", "system"]),
    id: z.string().min(1),
});
/** Money in integer minor units (e.g. cents) to avoid float rounding. */
export const MoneySchema = z.object({
    amountMinor: z.number().int(),
    currency: z.string().regex(/^[A-Z]{3}$/, "ISO-4217 currency code"),
});
/** Camera modes of the 3D world client (docs/PHASE2_PLAN.md §2.8). */
export const CAMERA_MODES = ["orbit", "blueprint", "first_person", "third_person", "fly"];
export const CameraModeSchema = z.enum(CAMERA_MODES);
/** Maximum number of ids in `Focus.view.visible`. */
export const FOCUS_MAX_VISIBLE = 32;
const FiniteVec3Schema = z.tuple([z.number(), z.number(), z.number()]);
/**
 * A stable part id of a scene node: "roof", "f2.wall.n", "body", "mesh:<gltf node name>".
 * The same rule as `PartIdSchema` in `@uzverse/world-scene`; the charset keeps part ids safe to
 * place in an agent prompt.
 */
export const FocusPartSchema = z
    .string()
    .max(64)
    .regex(/^[a-z0-9]+(?:[.:][a-z0-9_-]+)*$/i, "must be a part id like 'f2.wall.n'");
/**
 * What the user is looking at or clicked in the 3D world (a raycast hit), read at command time.
 * `point` is in world space (meters, Y up); `normal` is the hit face normal; `distance` is the
 * ray length from the camera.
 */
export const FocusTargetSchema = z.object({
    entityId: IdSchema,
    part: FocusPartSchema.optional(),
    point: FiniteVec3Schema.optional(),
    normal: FiniteVec3Schema.optional(),
    distance: z.number().nonnegative().optional(),
});
/** The world client's view state at command time. Gaze is never streamed, only sent here. */
export const FocusViewSchema = z.object({
    mode: CameraModeSchema,
    /** The placement the user is inside of (interior cutaway), if any. */
    insideId: IdSchema.optional(),
    floorLevel: z.number().int().optional(),
    /** A few prominent visible entity ids (nearest first). */
    visible: z.array(IdSchema).max(FOCUS_MAX_VISIBLE).optional(),
});
/**
 * The entity the client is focused on, sent with every command (DESIGN §1 rule 8).
 * `camera`, `target` and `view` are filled by the Phase 2 3D client; Phase 1 payloads
 * (`entityId`, `selection`, `camera`) parse unchanged.
 */
export const FocusSchema = z.object({
    entityId: IdSchema.nullable(),
    selection: z.array(IdSchema).optional(),
    camera: z
        .object({
        position: z.tuple([z.number(), z.number(), z.number()]),
        rotation: z.tuple([z.number(), z.number(), z.number(), z.number()]),
    })
        .optional(),
    target: FocusTargetSchema.optional(),
    view: FocusViewSchema.optional(),
});
/** License metadata stored with every external asset and voice. */
export const LicenseRecordSchema = z.object({
    sourceUrl: z.string().min(1),
    license: z.string().min(1),
    author: z.string().optional(),
    attribution: z.string().optional(),
    retrievedAt: TimestampSchema,
});
/** Reference to a content-addressed asset under `$UZVERSE_DATA/assets`. */
export const AssetRefSchema = z.object({
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    mediaType: z.string().min(1),
    name: z.string().min(1),
    sizeBytes: z.number().int().nonnegative(),
    license: LicenseRecordSchema.optional(),
});
/** Consent from the owner of a voice (or likeness) for cloning. */
export const ConsentRecordSchema = z.object({
    subjectUserId: IdSchema,
    grantedBy: ActorRefSchema,
    purpose: z.string().min(1),
    grantedAt: TimestampSchema,
    revokedAt: TimestampSchema.nullable(),
    evidence: AssetRefSchema.optional(),
});
//# sourceMappingURL=common.js.map